import { convert, formatCents } from "@ohc-price/shared";
import type { Language } from "./api";

/** US cents as PriceCharting gives them. */
export function usd(cents: number | null | undefined): string {
  if (cents == null) return "-";
  return "US$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Singapore cents. */
export function sgdCents(cents: number | null | undefined): string {
  if (cents == null) return "-";
  return formatCents(cents);
}

/** US cents in Singapore dollars at the given rate, the way the shared code converts. */
export function sgd(cents: number | null | undefined, rate: string | null | undefined): string {
  if (cents == null || !rate) return "-";
  return formatCents(convert(cents, rate));
}

export const LANGUAGE_LABEL: Record<Language, string> = { en: "EN", ja: "JP", zh: "CN", ko: "KR" };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A plain date, "2026-10-04", as "4 Oct 2026". Read from the text, never through a Date. */
export function day(text: string | null | undefined): string {
  if (!text) return "-";
  const [y, m, d] = text.split("-");
  return `${Number(d)} ${MONTHS[Number(m) - 1] ?? ""} ${y}`;
}

/** "4 Oct", for lists where the year is plain. */
export function shortDay(text: string | null | undefined): string {
  if (!text) return "-";
  const [, m, d] = text.split("-");
  return `${Number(d)} ${MONTHS[Number(m) - 1] ?? ""}`;
}

export function cardNumber(number: string | null, total: string | null): string {
  if (!number) return "";
  return total ? `${number}/${total}` : `#${number}`;
}

function sgParts(iso: string): { date: string; time: string } | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    date: d.toLocaleDateString("en-CA", { timeZone: "Asia/Singapore" }),
    time: d.toLocaleTimeString("en-GB", { timeZone: "Asia/Singapore", hour: "2-digit", minute: "2-digit" }),
  };
}

/** A server timestamp (ISO, UTC) as the Singapore date, "2026-10-19". */
export function sgDate(iso: string | null | undefined): string | null {
  return iso ? (sgParts(iso)?.date ?? null) : null;
}

/** A server timestamp as the Singapore day, "19 Oct 2026". */
export function sgDay(iso: string | null | undefined): string {
  return day(sgDate(iso));
}

/** A server timestamp as Singapore day and time, "19 Oct 2026, 08:40". */
export function sgTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const p = sgParts(iso);
  return p ? `${day(p.date)}, ${p.time}` : "-";
}

/** "just now", "12 min ago", "3 h ago", "2 days ago". */
export function ago(iso: string | null | undefined): string {
  if (!iso) return "never";
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (Number.isNaN(minutes)) return "-";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/** "Today", "Yesterday" or the day, for grouping a list by Singapore date. */
export function dayHeading(date: string): string {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Singapore" });
  const yesterday = new Date(Date.now() - 86_400_000).toLocaleDateString("en-CA", { timeZone: "Asia/Singapore" });
  if (date === today) return "Today";
  if (date === yesterday) return "Yesterday";
  return day(date);
}
